import * as React from 'react';

/**
 * Reflects `value` only after it has stopped changing for `delayInMilliseconds`. Pair with a local
 * `useState` for immediate visual feedback: the caller renders the local state directly, and reacts
 * to this debounced value (e.g. in a `useEffect`) to drive a costly side effect such as a
 * server-filtered fetch.
 */
function useDebounce<Value>(value: Value, delayInMilliseconds: number): Value {
	const [debouncedValue, setDebouncedValue] = React.useState<Value>(value);

	React.useEffect(() => {
		const timeoutId = window.setTimeout(() => {
			setDebouncedValue(value);
		}, delayInMilliseconds);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [value, delayInMilliseconds]);

	return debouncedValue;
}

export default useDebounce;
